import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Helmet } from "react-helmet";
import { Calendar, User, Share2, ArrowRight, Clock, Tag, Briefcase, ExternalLink, Sparkles, TrendingUp } from 'lucide-react';
import RenderGutenberg from './../components/RenderGutenberg';
import Loader from '../components/Loader';

const SinglePost = () => {
    const { slug } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true)
    const [details, setDetails] = useState(null)
    const [error, setError] = useState(null)
    const [showShare, setShowShare] = useState(false)

    const defaultMetaTags = {
        title: 'Connected Bytes Software Service',
        description: 'Explore our case studies and insights',
        image: 'https://utfs.io/f/wzoc0SJJ8iMqmv8tcTUCV0hUWxjMFoDS38r1AKmZ5kftGwsN',
        url: 'https://connectedbytes.in'
    }

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`https://emr.metadei.in/wp-json/wp/v2/posts?slug=${slug}`);
                const postData = Array.isArray(res.data) ? res.data[0] : res.data;
                if (postData) {
                    setDetails(postData);
                    setLoading(false);
                } else {
                    setError(new Error('Case study not found'));
                    setLoading(false);
                }
            } catch (fallbackErr) {
                console.error(fallbackErr);
                setError(fallbackErr);
                setLoading(false);
            }
        };

        fetchData()
        window.scrollTo(0, 0);
    }, [slug])

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('en-US', options);
    }

    const getReadTime = (content) => {
        if (!content) return '5 min read';
        const words = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
        const readTime = Math.ceil(words / 200);
        return `${readTime} min read`;
    }

    // Helper function to check if SCF data exists
    const hasSCFData = () => {
        return details?.acf && Object.keys(details.acf).length > 0 &&
            !(details.acf.length === 0 || Object.keys(details.acf).length === 0);
    }

    // Get client name from SCF
    const getClientName = () => {
        if (hasSCFData() && details.acf.client_name) {
            return details.acf.client_name;
        }
        return details.title?.rendered || '';
    }

    // Get industry from SCF
    const getIndustry = () => {
        if (hasSCFData() && details.acf.industry) {
            return details.acf.industry;
        }
        return '';
    }

    // Get challenge content from SCF
    const getChallenge = () => {
        if (hasSCFData() && details.acf.challenge) {
            return details.acf.challenge;
        }
        return null;
    }

    // Get solution content from SCF
    const getSolution = () => {
        if (hasSCFData() && details.acf.solution) {
            return details.acf.solution;
        }
        return null;
    }

    // Get results content from SCF
    const getResults = () => {
        if (hasSCFData() && details.acf.results) {
            return details.acf.results;
        }
        return null;
    }

    // Helper function to extract image URL from object or string
    const getImageUrl = (imageField) => {
        if (!imageField) return null;
        if (typeof imageField === 'string' && (imageField.startsWith('http') || imageField.startsWith('/'))) {
            return imageField.replace(/^http:\/\/emr\.metadei\.in/, 'https://emr.metadei.in');
        }
        if (typeof imageField === 'object' && imageField.url) {
            return imageField.url.replace(/^http:\/\/emr\.metadei\.in/, 'https://emr.metadei.in');
        }
        return null;
    }

    // Get featured image from SCF
    const getFeaturedImage = () => {
        if (hasSCFData()) {
            const featured = getImageUrl(details.acf.featured_image);
            if (featured) return featured;

            const seoImage = getImageUrl(details.acf.seo_image_posts);
            if (seoImage) return seoImage;
        }

        const embeddedImage = getImageUrl(details?._embedded?.['wp:featuredmedia']?.[0]?.source_url);
        if (embeddedImage) return embeddedImage;

        return 'https://via.placeholder.com/600x400?text=Case+Study+Image';
    }

    // Check if we have SCF content to show structured sections
    const hasStructuredContent = () => {
        return getChallenge() || getSolution() || getResults();
    }

    const getMetaTags = () => {
        if (loading || !details) return defaultMetaTags;
        return {
            title: details.title.rendered,
            description: details.acf?.SEO_excerpt_posts || details.excerpt?.rendered?.replace(/<[^>]*>/g, '').substring(0, 160),
            image: getFeaturedImage(),
            url: `https://connectedbytes.in/case-studies/${slug}`
        }
    }

    const metaTags = getMetaTags();

    const shareLinks = {
        twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(metaTags.title)}&url=${encodeURIComponent(metaTags.url)}`,
        linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(metaTags.url)}&title=${encodeURIComponent(metaTags.title)}`,
    }

    if (loading) {
        return <Loader />;
    }

    if (error || !details) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background text-foreground transition-colors duration-300">
                <div className="text-center max-w-md mx-auto px-4">
                    <div className="text-6xl mb-6">😕</div>
                    <h2 className="text-2xl font-bold font-heading mb-2">Case Study Target Not Found</h2>
                    <p className="text-muted-foreground mb-6 font-body">The architectural analysis you're looking for could not be located in our database.</p>
                    <button
                        onClick={() => navigate('/case-studies')}
                        className="px-8 py-3 bg-gradient-to-r from-primary to-accent text-primary-foreground rounded-full font-bold shadow-lg hover:shadow-[0_0_20px_rgba(var(--color-primary),0.5)] transition-all font-secondary"
                    >
                        Return to Case Studies
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>{`${metaTags.title} - Connected Bytes Software Service`}</title>
                <meta name="description" content={metaTags.description} />
                <meta property="og:title" content={`${metaTags.title} - Connected Bytes Software Service`} />
                <meta property="og:description" content={metaTags.description} />
                <meta property="og:image" content={metaTags.image} />
                <meta property="og:url" content={metaTags.url} />
                <meta property="og:type" content="article" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={`${metaTags.title} - Connected Bytes Software Service`} />
                <meta name="twitter:description" content={metaTags.description} />
                <meta name="twitter:image" content={metaTags.image} />
            </Helmet>

            <div className="bg-background text-foreground min-h-screen transition-colors duration-300">
                {/* Hero Section */}
                <div className="relative pt-36 pb-20 overflow-hidden border-b border-border">
                    <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 blur-[150px] rounded-full pointer-events-none translate-x-1/2" />
                    <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-accent/5 blur-[120px] rounded-full pointer-events-none -translate-x-1/2 translate-y-1/2" />

                    <div className="max-w-7xl mx-auto px-4 relative z-10">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                            {/* Left Content */}
                            <motion.div
                                initial={{ opacity: 0, x: -50, filter: 'blur(10px)' }}
                                animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                                transition={{ duration: 0.8, type: 'spring' }}
                            >
                                <span className="inline-block text-primary font-bold text-xs uppercase tracking-widest font-secondary mb-4 px-3 py-1 bg-primary/10 border border-primary/20 rounded-full">
                                    Case Study
                                </span>

                                <h1 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 leading-tight font-heading">
                                    {details.title.rendered}
                                </h1>



                                <p className="text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed font-body border-l-2 border-primary pl-4">
                                    {details.acf?.SEO_excerpt_posts || details.excerpt?.rendered?.replace(/<[^>]*>/g, '').substring(0, 200)}
                                </p>

                                {/* <div className="flex flex-wrap gap-6 text-sm font-bold text-foreground opacity-70 mb-10 font-secondary uppercase tracking-wider">
                                    <div className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-primary" />
                                        <span>{formatDate(details.date)}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Clock className="w-4 h-4 text-accent" />
                                        <span>{getReadTime(details.content?.rendered)}</span>
                                    </div>
                                    <button
                                        onClick={() => setShowShare(!showShare)}
                                        className="flex items-center gap-2 hover:text-primary transition-colors duration-300"
                                    >
                                        <Share2 className="w-4 h-4" />
                                        Broadcast
                                    </button>
                                </div> */}

                                {showShare && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="mt-6 flex flex-wrap gap-3"
                                    >
                                        <a href={shareLinks.twitter} target="_blank" rel="noopener noreferrer" className="px-5 py-2.5 bg-[#1DA1F2]/10 text-[#1DA1F2] border border-[#1DA1F2]/30 rounded-full text-sm font-bold hover:bg-[#1DA1F2] hover:text-white transition-colors duration-300">Twitter X</a>
                                        <a href={shareLinks.linkedin} target="_blank" rel="noopener noreferrer" className="px-5 py-2.5 bg-[#0077B5]/10 text-[#0077B5] border border-[#0077B5]/30 rounded-full text-sm font-bold hover:bg-[#0077B5] hover:text-white transition-colors duration-300">LinkedIn</a>
                                        <button onClick={() => { navigator.clipboard.writeText(window.location.href); alert('Link copied!'); }} className="px-5 py-2.5 bg-muted text-foreground border border-border rounded-full text-sm font-bold hover:bg-foreground hover:text-background transition-colors duration-300">Copy Link</button>
                                    </motion.div>
                                )}
                            </motion.div>

                            {/* Right Image */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95, filter: 'blur(20px)' }}
                                animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                                transition={{ duration: 0.8, delay: 0.2, type: 'spring' }}
                                className="relative lg:h-[500px] w-full flex items-center justify-center" // added flex/center here
                            >
                                {/* Background Glow */}
                                <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-accent/20 rounded-3xl blur-2xl transform scale-105 pointer-events-none -z-10" />

                                {/* Container */}
                                <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-card border border-border h-full w-full flex items-center justify-center p-6 group">
                                    <img
                                        src={getFeaturedImage()}
                                        alt={details.title.rendered}
                                        // Added block and mx-auto for safety
                                        className="max-w-full max-h-full object-contain block mx-auto group-hover:scale-105 transition-transform duration-700"
                                    />

                                    {/* Overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-tr from-primary/10 to-transparent pointer-events-none mix-blend-overlay" />
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>

                {/* Content Section - Full Width */}
                <div className="max-w-7xl mx-auto px-4 py-6 relative z-10">
                    {hasStructuredContent() ? (
                        // Structured content from SCF fields
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                            className="space-y-6"
                        >
                            {/* Challenge Section */}
                            {getChallenge() && (
                                <section className="relative group">
                                    <div className="absolute inset-0 bg-primary/5 rounded-3xl -z-10 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                    <div className="bg-card/50 backdrop-blur-md border border-primary/20 rounded-3xl p-8 md:p-12 shadow-sm transition-all duration-300">
                                        <div className="flex items-center gap-4 mb-8">
                                            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center border border-primary/20">
                                                <Briefcase className="w-6 h-6 text-primary" />
                                            </div>
                                            <h2 className="text-3xl font-black font-heading text-foreground">
                                                The Architecture Challenge
                                            </h2>
                                        </div>
                                        <div className="prose prose-lg dark:prose-invert max-w-none prose-p:font-body prose-p:leading-relaxed prose-p:text-muted-foreground prose-strong:text-foreground">
                                            <div dangerouslySetInnerHTML={{ __html: getChallenge() }} />
                                        </div>
                                    </div>
                                </section>
                            )}

                            {/* Solution Section */}
                            {getSolution() && (
                                <section className="relative group">
                                    <div className="absolute inset-0 bg-accent/5 rounded-3xl -z-10 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                    <div className="bg-card/50 backdrop-blur-md border border-accent/20 rounded-3xl p-8 md:p-12 shadow-sm transition-all duration-300">
                                        <div className="flex items-center gap-4 mb-8">
                                            <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center border border-accent/20">
                                                <Sparkles className="w-6 h-6 text-accent" />
                                            </div>
                                            <h2 className="text-3xl font-black font-heading text-foreground">
                                                Our Solution Matrix
                                            </h2>
                                        </div>
                                        <div className="prose prose-lg dark:prose-invert max-w-none prose-p:font-body prose-p:leading-relaxed prose-p:text-muted-foreground prose-strong:text-foreground">
                                            <div dangerouslySetInnerHTML={{ __html: getSolution() }} />
                                        </div>
                                    </div>
                                </section>
                            )}

                            {/* Results Section */}
                            {getResults() && (
                                <section className="relative group">
                                    <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-accent/10 rounded-3xl -z-10 blur-xl opacity-50 group-hover:opacity-100 transition-opacity duration-500" />
                                    <div className="bg-gradient-to-br from-primary/5 to-accent/5 backdrop-blur-md border border-border rounded-3xl p-8 md:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.12)] transition-all duration-300 relative overflow-hidden">
                                        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-tr from-primary/20 to-accent/20 blur-2xl rounded-full" />
                                        <div className="flex items-center gap-4 mb-8 relative z-10">
                                            <div className="w-12 h-12 rounded-xl bg-background flex items-center justify-center shadow-inner border border-border">
                                                <TrendingUp className="w-6 h-6 text-foreground" />
                                            </div>
                                            <h2 className="text-3xl font-black font-heading text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
                                                Execution Results
                                            </h2>
                                        </div>
                                        <div className="prose prose-lg dark:prose-invert max-w-none prose-p:font-body prose-p:leading-relaxed prose-p:text-foreground/90 prose-strong:text-foreground relative z-10">
                                            <div dangerouslySetInnerHTML={{ __html: getResults() }} />
                                        </div>
                                    </div>
                                </section>
                            )}
                        </motion.div>
                    ) : (
                        // Fallback to original content.rendered
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                            className="bg-card/30 backdrop-blur-sm p-8 md:p-16 border border-border rounded-[2.5rem] shadow-sm"
                        >
                            <div className="prose prose-lg dark:prose-invert max-w-none prose-headings:font-heading prose-headings:font-black prose-h1:text-4xl prose-h2:text-3xl prose-h3:text-2xl prose-p:font-body prose-p:leading-relaxed prose-a:text-primary prose-a:font-bold prose-img:rounded-3xl prose-img:shadow-2xl prose-img:border prose-img:border-border">
                                <RenderGutenberg content={details.content.rendered} />
                            </div>
                        </motion.div>
                    )}
                </div>

                {/* CTA Section */}
                <div className="py-12 relative overflow-hidden border-t border-border">
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-primary/5 pointer-events-none" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl h-[400px] bg-primary/20 blur-[150px] rounded-full pointer-events-none" />

                    <div className="max-w-4xl mx-auto text-center px-4 relative z-10">
                        <h2 className="text-4xl md:text-5xl font-black font-heading mb-6">Initialize Your Transformation</h2>
                        <p className="text-xl text-muted-foreground mb-10 font-body max-w-2xl mx-auto">
                            Deploy our engineering elite to architect your next technological breakthrough.
                        </p>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => navigate('/contact')}
                            className="px-10 py-5 bg-gradient-to-r from-primary to-accent text-primary-foreground rounded-full font-bold shadow-[0_0_20px_rgba(var(--color-primary),0.3)] hover:shadow-[0_0_30px_rgba(var(--color-primary),0.5)] transition-all duration-300 inline-flex items-center gap-3 font-secondary text-lg"
                        >
                            Engage Engineering Team <ArrowRight className="w-6 h-6" />
                        </motion.button>
                    </div>
                </div>

                {/* Related Case Studies */}
                <div className="bg-muted/10 py-24 border-t border-border">
                    <div className="max-w-7xl mx-auto px-4 text-center">
                        <h2 className="text-3xl md:text-4xl font-black font-heading mb-4">Analyze More Case Studies</h2>
                        <div className="w-24 h-1 bg-gradient-to-r from-primary to-accent mx-auto rounded-full mb-10 shadow-[0_0_10px_rgba(var(--color-primary),0.5)]" />
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => navigate('/case-studies')}
                            className="px-8 py-4 bg-background text-foreground border-2 border-border hover:border-primary hover:text-primary rounded-full font-bold shadow-sm transition-all duration-300 inline-flex items-center gap-3 font-secondary text-lg"
                        >
                            View Architecture Archives <ArrowRight className="w-5 h-5" />
                        </motion.button>
                    </div>
                </div>
            </div>
        </>
    )
}

export default SinglePost